import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ArrowRight, Star, Users, Shield, Calendar, Clock, Package, RotateCcw, Infinity, ChevronDown } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { signOutGlobally } from "@/lib/auth-utils";
import { safeSetItem } from "@/lib/safe-storage";
import { useToast } from "@/hooks/use-toast";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselApi,
} from "@/components/ui/carousel";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
 } from "@/components/ui/collapsible";
import { NotificationBell } from "@/components/NotificationBell";
import { useIsMobile } from "@/hooks/use-mobile";
import MentorSearch from "@/components/MentorSearch";
import MentorList from "@/components/MentorList";
import CookieConsent from "@/components/CookieConsent";
import ComplianceFooter from "@/components/ComplianceFooter";
import { AIMentorChatbot } from "@/components/AIMentorChatbot";

const Index = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [api, setApi] = useState<CarouselApi>();
  const [whyChooseOpen, setWhyChooseOpen] = useState(false);
  const [creditsWorkOpen, setCreditsWorkOpen] = useState(false);
  const [creditPackagesOpen, setCreditPackagesOpen] = useState(false);
  const isMobile = useIsMobile();

  const handleDashboardRedirect = async () => {
    try {
      console.log('🚀 NUKE MODE: Starting dashboard redirect');
      
      if (!user) {
        console.log('❌ NUKE MODE: No user found');
        return;
      }

      // ALWAYS fetch fresh from database - NEVER trust cached state
      console.log('🔥 NUKE MODE: Fetching fresh role from database...');
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', user.id)
        .single();
      
      if (error || !profile?.role) {
        console.error('💥 NUKE MODE: Database fetch failed:', error);
        toast({
          title: "Error",
          description: "Unable to determine your access level. Please try again.",
          variant: "destructive",
        });
        return;
      }

      const role = profile.role;
      console.log('💥 NUKE MODE: Fresh role from DB:', role);
      
      // Direct navigation based on fresh role - NO CACHE
      let targetRoute = '/dashboard/student'; // fallback
      
      if (role === 'admin') {
        targetRoute = '/dashboard/admin';
      } else if (role === 'mentor') {
        targetRoute = '/dashboard/mentor';
      } else if (role === 'student') {
        targetRoute = '/dashboard/student';
      }
      
      console.log('🎯 NUKE MODE: Navigating to:', targetRoute);
      navigate(targetRoute);
      
      // Update cached role AFTER navigation
      setUserRole(role);
      
    } catch (error) {
      console.error('💥 NUKE MODE: Complete failure:', error);
      navigate('/dashboard/student');
    }
  };

  const handleSignOut = async () => {
    try {
      console.log('🚀 NUKE MODE: Starting sign out');
      setAuthLoading(true);
      
      // NUKE all auth state first
      setUser(null);
      setUserRole(null);
      
      // Clean up auth state
      await signOutGlobally();
      
      console.log('💥 NUKE MODE: Sign out complete, forcing reload');
      // Force complete page reload to clear all state
      window.location.href = '/';
      
    } catch (error) {
      console.error('💥 NUKE MODE: Sign out error:', error);
      // Force reload even on error
      window.location.href = '/';
    }
  };

  // NUKED AUTH SYSTEM - Completely rebuilt
  useEffect(() => {
    document.title = "Ascendry - Expert Mentorship On Demand";
    
    let mounted = true;
    
    const initializeAuth = async () => {
      try {
        console.log('🚀 Initializing auth system');
        setAuthLoading(true);
        
        // Hard 8s timeout for entire auth init to prevent UI freeze
        const authInitTimeout = setTimeout(() => {
          console.warn('⚠️ Auth init timeout - unblocking UI');
          if (mounted) {
            setAuthLoading(false);
          }
        }, 8000);
        
        // Get current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          clearTimeout(authInitTimeout);
          if (mounted) setAuthLoading(false);
          return;
        }
        
        if (!session?.user) {
          console.log('No session found');
          clearTimeout(authInitTimeout);
          if (mounted) {
            setUser(null);
            setUserRole(null);
            setAuthLoading(false);
          }
          return;
        }
        
        console.log('Session found, fetching role for:', session.user.email);
        
        // Fetch role with 5s timeout
        try {
          const profilePromise = supabase
            .from('profiles')
            .select('role')
            .eq('user_id', session.user.id)
            .single();
          
          const { data: profile, error: profileError } = await Promise.race([
            profilePromise,
            new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Profile timeout')), 5000))
          ]);
          
          if (profileError) {
            console.error('Profile fetch error:', profileError);
          }
          
          const role = profile?.role || null;
          
          if (mounted) {
            setUser(session.user);
            setUserRole(role);
          }
        } catch (profileErr) {
          console.warn('Profile fetch failed/timed out, continuing without role');
          if (mounted) {
            setUser(session.user);
            setUserRole(null);
          }
        }
        
        clearTimeout(authInitTimeout);
        if (mounted) setAuthLoading(false);
        
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setUser(null);
          setUserRole(null);
          setAuthLoading(false);
        }
      }
    };
    
    // Initialize auth
    initializeAuth();
    
    // Listen for auth changes - FIXED VERSION
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('💥 FIXED MODE: Auth state change:', event);
      
      if (!mounted) return;
      
      if (event === 'SIGNED_OUT' || !session) {
        console.log('💥 FIXED MODE: User signed out');
        setUser(null);
        setUserRole(null);
        setAuthLoading(false);
        return;
      }
      
      if (event === 'TOKEN_REFRESHED') {
        // Silently update user without showing loading state
        // This prevents the page from flashing on every tab switch
        console.log('🔄 Token refreshed - silent update');
        setUser(session.user);
        return;
      }
      
      if (event === 'SIGNED_IN') {
        console.log('🔥 FIXED MODE: Auth event - deferring role fetch');
        setAuthLoading(true);
        
        // Defer Supabase calls to prevent deadlock
        setTimeout(async () => {
          try {
            const { data: profile } = await supabase
              .from('profiles')
              .select('role')
              .eq('user_id', session.user.id)
              .single();
            
            const role = profile?.role || null;
            console.log('💥 FIXED MODE: Fresh role from auth change:', role);
            
            setUser(session.user);
            setUserRole(role);
          } catch (error) {
            console.error('💥 FIXED MODE: Error fetching role on auth change:', error);
          } finally {
            setAuthLoading(false);
          }
        }, 0);
      }
    });
    
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Auto-cycle carousel every 5 seconds
  useEffect(() => {
    if (!api) {
      return;
    }

    const interval = setInterval(() => {
      api.scrollNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [api]);

  const handlePurchase = async (credits: number, amount: number) => {
    // Check if user is authenticated first
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to purchase credits.",
      });
      navigate('/auth/student');
      return;
    }

    try {
      // Ensure we have a valid session before calling edge function
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to purchase tokens.",
          variant: "destructive",
        });
        navigate('/auth/student');
        return;
      }

      toast({
        title: "Redirecting to checkout...",
        description: "You'll be redirected to Stripe to complete your purchase.",
      });

      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: { credits, amount }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }

      if (data?.url) {
        // Use location.href instead of window.open for better reliability
        window.location.href = data.url;
      } else {
        throw new Error("No payment URL returned");
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      toast({
        title: "Payment Error",
        description: error?.message || "Failed to create payment session. Please try again or contact support.",
        variant: "destructive",
      });
    }
  };


  return (
    <div className="min-h-screen bg-gradient-dark" style={{ minHeight: '100dvh', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* Header */}
      <header className="border-b border-border obsidian-glass" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
        <div className="container mx-auto px-4 pb-4">
          {/* Mobile Layout */}
          <div className="flex md:hidden justify-between items-center">
            <img 
              src="/lovable-uploads/e601b2a4-d63b-447f-b8f5-3e03625e19d6.png" 
              alt="Ascendry" 
              className="h-8 w-auto"
            />
            {user ? (
              <Button 
                variant="outline" 
                size="sm" 
                className="text-xs px-3 py-2" 
                onClick={handleSignOut}
              >
                Sign Out
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="text-xs px-3 py-2" asChild>
                  <Link to="/auth/mentor">Mentor</Link>
                </Button>
                <Button className="energy-button text-xs px-3 py-2" size="sm" asChild>
                  <Link to="/auth/student">Get Started</Link>
                </Button>
              </div>
            )}
          </div>
          
          {/* Mobile Auth Links */}
          {user && (
            <div className="flex md:hidden justify-center items-center gap-2 mt-3">
              <NotificationBell />
              <Button variant="outline" size="sm" className="text-xs px-3 py-2 flex-1 max-w-40" onClick={handleDashboardRedirect}>
                Dashboard
              </Button>
            </div>
          )}
          
          {/* Mobile Search Bar */}
          <div className="md:hidden mt-3">
            <form onSubmit={(e) => {
              e.preventDefault();
              const searchQuery = (e.target as HTMLFormElement).searchInput.value;
              if (searchQuery.trim()) {
                navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
              }
            }} className="relative">
              <input
                type="text"
                name="searchInput"
                placeholder="Search"
                className="w-full pl-10 pr-16 py-2 text-sm bg-card/50 border border-border/50 rounded-full focus:bg-card/80 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <Button 
                type="submit"
                size="sm"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 rounded-full px-3 h-7 text-xs"
              >
                Search
              </Button>
            </form>
          </div>
          
          {/* Desktop Layout */}
          <div className="hidden md:flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img 
                src="/lovable-uploads/e601b2a4-d63b-447f-b8f5-3e03625e19d6.png" 
                alt="Ascendry" 
                className="h-8 w-auto"
              />
              <span className="text-2xl font-bold text-glow">
                Ascendry
              </span>
            </div>
            {user ? (
              <div className="flex items-center space-x-4">
                <NotificationBell />
                <Button variant="outline" size="sm" onClick={handleDashboardRedirect}>
                  Go to Dashboard
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleSignOut}
                >
                  Sign Out
                </Button>
              </div>
            ) : (
              <div className="flex space-x-4">
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/auth/mentor">Mentor Login</Link>
                  </Button>
                </div>
                <Button className="energy-button" asChild>
                  <Link to="/auth/student">Get Started</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-12 pb-6 px-4">
        <div className="container mx-auto text-center">
          <div className="mb-8">
            <img 
              src="/lovable-uploads/ab1c0404-1d7a-4d79-8fbf-75bf3555226b.png" 
              alt="Ascendry Logo" 
              className="h-24 w-auto mx-auto"
            />
          </div>
          <h2 className="text-4xl font-bold mb-6 text-glow">
            Connect with Expert Mentors on Ascendry
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Book personalized one-on-one sessions with industry professionals using our token-based platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md sm:max-w-none mx-auto">
            <Button size="lg" className="energy-button energy-glow w-full sm:w-auto" asChild>
              <Link to="/auth/student">
                <span className="hidden sm:inline">Student Login</span>
                <span className="sm:hidden">Student Login</span>
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="hidden sm:flex w-full sm:w-auto" asChild>
              <Link to="/auth/mentor">
                <span className="hidden sm:inline">Become a Mentor</span>
                <span className="sm:hidden">Be a Mentor</span>
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Search and Mentor Carousel */}
      <section className="py-4 px-4 pb-8">
        <div className="container mx-auto">
          <MentorSearch onBookMentor={(mentor) => {
            if (user && userRole === 'student') {
              // Store booking intent and navigate to student dashboard
              safeSetItem('startBookingForMentor', JSON.stringify({
                id: mentor.id,
                first_name: mentor.first_name,
                last_name: mentor.last_name,
                specialty: mentor.specialty,
                hourly_rate: mentor.hourly_rate,
                profile_image_url: mentor.profile_image_url,
                tier: mentor.tier
              }));
              navigate('/dashboard/student');
            } else if (user) {
              // Other roles redirect to dashboard
              handleDashboardRedirect();
            } else {
              // Not logged in - store booking intent and go to student auth
              safeSetItem('startBookingForMentor', JSON.stringify({
                id: mentor.id,
                first_name: mentor.first_name,
                last_name: mentor.last_name,
                specialty: mentor.specialty,
                hourly_rate: mentor.hourly_rate,
                profile_image_url: mentor.profile_image_url,
                tier: mentor.tier
              }));
              safeSetItem('redirectAfterLogin', '/dashboard/student');
              navigate('/auth/student');
            }
          }} />
          <div className="mt-4 text-center">
            <Button size="lg" className="energy-button energy-glow" asChild>
              <Link to="/mentors">
                See All Mentors
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Student Success Stories */}
      <section className="py-12 px-4 bg-black">
        <div className="container mx-auto">
          <div className="text-center mb-10">
            <h3 className="text-4xl font-bold mb-4 text-glow">Our Students Perform Worldwide</h3>
            <p className="text-xl text-muted-foreground">From bedroom producers to main stage performers</p>
          </div>
          
          {/* Student Success Carousel */}
          <div className="relative">
            <Carousel
              setApi={setApi}
              opts={{
                align: "start",
                loop: true,
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-2 md:-ml-4">
                {/* SCARLET WIDOW */}
                <CarouselItem className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3">
                  <div className="h-full">
                    <AspectRatio ratio={4/5} className="overflow-hidden rounded-lg shadow-lg energy-glow hover:scale-105 transition-transform duration-300">
                      <img 
                        src="/lovable-uploads/e98721b2-d44b-4bb8-ba0b-b28485a54567.png" 
                        alt="SCARLET WIDOW - Performing with Crankdat and Wooli at the legendary Radius Nightclub in Chicago"
                        className="w-full h-full object-cover"
                      />
                    </AspectRatio>
                  </div>
                </CarouselItem>

                {/* KAMI KHAOS */}
                <CarouselItem className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3">
                  <div className="h-full">
                    <AspectRatio ratio={4/5} className="overflow-hidden rounded-lg shadow-lg energy-glow hover:scale-105 transition-transform duration-300">
                      <img 
                        src="/lovable-uploads/c5a742f2-562f-4862-9626-ccbf4f2c8b8c.png" 
                        alt="KAMI KHAOS - Performing with Kaivon at Area15 in Las Vegas"
                        className="w-full h-full object-cover"
                      />
                    </AspectRatio>
                  </div>
                </CarouselItem>

                {/* XZEN */}
                <CarouselItem className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3">
                  <div className="h-full">
                    <AspectRatio ratio={4/5} className="overflow-hidden rounded-lg shadow-lg energy-glow hover:scale-105 transition-transform duration-300">
                      <img 
                        src="/lovable-uploads/xzen-updated.jpg" 
                        alt="XZEN - Quickly building a fanbase, signed to multiple labels and booked to open for ITS MURPH!"
                        className="w-full h-full object-cover"
                      />
                    </AspectRatio>
                  </div>
                </CarouselItem>


                {/* STOIG */}
                <CarouselItem className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3">
                  <div className="h-full">
                    <AspectRatio ratio={4/5} className="overflow-hidden rounded-lg shadow-lg energy-glow hover:scale-105 transition-transform duration-300">
                      <img 
                        src="/lovable-uploads/2b1e81e8-ea73-458a-881a-896c30adb297.png" 
                        alt="STOIG - Got signed to Bear Grillz' record label Rude Service with his single 'Jet Stream'"
                        className="w-full h-full object-cover"
                      />
                    </AspectRatio>
                  </div>
                </CarouselItem>

                {/* DYLANDYLANDYLAN */}
                <CarouselItem className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3">
                  <div className="h-full">
                    <AspectRatio ratio={4/5} className="overflow-hidden rounded-lg shadow-lg energy-glow hover:scale-105 transition-transform duration-300">
                      <img 
                        src="/lovable-uploads/f00b679c-a899-443d-91d3-a85729d801fd.png" 
                        alt="DYLANDYLANDYLAN - Performed at Woodkey Music Festival, Elektricity and more in Michigan"
                        className="w-full h-full object-cover"
                      />
                    </AspectRatio>
                  </div>
                </CarouselItem>

                {/* STEEZUS CHRXST */}
                <CarouselItem className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3">
                  <div className="h-full">
                    <AspectRatio ratio={4/5} className="overflow-hidden rounded-lg shadow-lg energy-glow hover:scale-105 transition-transform duration-300">
                      <img 
                        src="/lovable-uploads/steezus-chrxst-updated.jpg" 
                        alt="STEEZUS - Booked to open for Getter on his return tour, producing solid originals just a few months in"
                        className="w-full h-full object-cover"
                      />
                    </AspectRatio>
                  </div>
                </CarouselItem>
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex energy-button border-red-500/30 bg-black/50 hover:bg-red-900/50" />
              <CarouselNext className="hidden md:flex energy-button border-red-500/30 bg-black/50 hover:bg-red-900/50" />
            </Carousel>
          </div>

          <div className="mt-12 text-center">
            <Button 
              size="lg" 
              variant="outline" 
              className="w-full sm:w-auto max-w-xs mx-auto" 
              onClick={user ? handleDashboardRedirect : () => navigate('/auth/student')}
            >
              <span className="mr-2">Ready to be next?</span>
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="relative py-20 px-4 overflow-hidden">
        {/* Background gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-primary/5 to-background" />
        
        {/* Decorative glow effects */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
        
        <div className="container mx-auto max-w-5xl relative z-10">
          {/* Decorative top border */}
          <div className="flex items-center justify-center mb-12">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-primary" />
            <Star className="w-6 h-6 text-primary mx-4 animate-pulse" />
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-primary" />
          </div>

          <div className="relative">
            {/* Main content card */}
            <div className="bg-card/60 backdrop-blur-xl border border-primary/20 rounded-2xl p-8 md:p-14 shadow-2xl">
              {/* Accent corners */}
              <div className="absolute top-0 left-0 w-20 h-20 border-t-2 border-l-2 border-primary/30 rounded-tl-2xl" />
              <div className="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-primary/30 rounded-br-2xl" />
              
              <div className="text-center space-y-6">
                <h3 className="text-3xl md:text-5xl font-black mb-6 text-primary leading-tight">
                  Our Mission
                </h3>
                <p className="text-base md:text-2xl leading-relaxed text-foreground/90 font-medium">
                  <span className="text-primary font-bold">Ascendry</span> empowers aspiring artists and creators to learn directly from{" "}
                  <span className="text-primary/90 font-semibold">Grammy Award-winning producers</span>,{" "}
                  <span className="text-primary/90 font-semibold">platinum and gold-certified professionals</span>,{" "}
                  <span className="text-primary/90 font-semibold">Billboard-charting artists</span>, and{" "}
                  <span className="text-primary/90 font-semibold">actively touring industry veterans</span>.
                </p>
                <p className="text-sm md:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto pt-2 md:pt-4">
                  Through our flexible token-based platform, students gain personalized one-on-one mentorship from highly 
                  successful mentors who are at the top of their craft—<span className="text-foreground font-semibold">transforming ambition into real-world achievement</span>, 
                  one session at a time.
                </p>
              </div>
            </div>
          </div>

          {/* Decorative bottom border */}
          <div className="flex items-center justify-center mt-12">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-primary" />
            <Star className="w-6 h-6 text-primary mx-4 animate-pulse" />
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-primary" />
          </div>
        </div>
      </section>

      {/* All Mentors List - Hidden on mobile */}
      <section className="hidden md:block py-12 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold mb-2 text-glow">Our Mentors</h3>
            <p className="text-muted-foreground">Connect with industry professionals</p>
          </div>
          <MentorList onBookMentor={(mentor) => {
            if (user && userRole === 'student') {
              // Store booking intent and navigate to student dashboard
              safeSetItem('startBookingForMentor', JSON.stringify({
                id: mentor.id,
                first_name: mentor.first_name,
                last_name: mentor.last_name,
                specialty: mentor.specialty,
                hourly_rate: mentor.hourly_rate,
                profile_image_url: mentor.profile_image_url,
                tier: mentor.tier
              }));
              navigate('/dashboard/student');
            } else if (user) {
              // Other roles redirect to dashboard
              handleDashboardRedirect();
            } else {
              // Not logged in - store booking intent and go to student auth
              safeSetItem('startBookingForMentor', JSON.stringify({
                id: mentor.id,
                first_name: mentor.first_name,
                last_name: mentor.last_name,
                specialty: mentor.specialty,
                hourly_rate: mentor.hourly_rate,
                profile_image_url: mentor.profile_image_url,
                tier: mentor.tier
              }));
              safeSetItem('redirectAfterLogin', '/dashboard/student');
              navigate('/auth/student');
            }
          }} />
        </div>
      </section>

      {/* Collapsible Sections */}
      <section className="py-10 px-4">
        <div className="container mx-auto max-w-4xl space-y-6">
          {/* Why Choose Ascendry */}
          <Collapsible open={whyChooseOpen} onOpenChange={setWhyChooseOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between text-left p-4 sm:p-6 h-auto obsidian-card hover:bg-muted/5">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-glow pr-2">Why Ascendry?</h3>
                <ChevronDown className={`h-5 w-5 sm:h-6 sm:w-6 text-primary transition-transform duration-200 flex-shrink-0 ${whyChooseOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <Card className="obsidian-card">
                  <CardHeader className="text-center">
                    <Users className="w-12 h-12 text-primary mx-auto mb-4" />
                    <CardTitle className="text-energy">Expert Mentors</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-center">
                      Connect with verified industry professionals who have years of experience in their fields.
                    </p>
                  </CardContent>
                </Card>
                <Card className="obsidian-card">
                  <CardHeader className="text-center">
                    <Calendar className="w-12 h-12 text-primary mx-auto mb-4" />
                    <CardTitle className="text-energy">Flexible Booking</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-center">
                      Book sessions at your convenience with our easy-to-use calendar system and instant confirmations.
                    </p>
                  </CardContent>
                </Card>
                <Card className="obsidian-card">
                  <CardHeader className="text-center">
                    <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
                    <CardTitle className="text-energy">Secure Payments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-center">
                      Pay securely with our credit system. Purchase bundles and save, with BNPL options available.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* How Credits Work */}
          <Collapsible open={creditsWorkOpen} onOpenChange={setCreditsWorkOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between text-left p-4 sm:p-6 h-auto obsidian-card hover:bg-muted/5">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-glow pr-2">Our Token System</h3>
                <ChevronDown className={`h-5 w-5 sm:h-6 sm:w-6 text-primary transition-transform duration-200 flex-shrink-0 ${creditsWorkOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4 pt-4">
              <div className="grid grid-cols-1 gap-4 max-w-2xl mx-auto">
                <Card className="obsidian-card">
                  <CardContent className="flex items-center p-6">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mr-4 energy-glow flex-shrink-0">
                      <Clock className="w-6 h-6 text-energy" />
                    </div>
                    <div className="text-left">
                      <h4 className="text-lg font-semibold text-energy mb-1">Simple Pricing</h4>
                      <p className="text-sm text-muted-foreground">
                        One token equals one dollar of mentoring credit. Clear, transparent, no hidden fees.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="obsidian-card">
                  <CardContent className="flex items-center p-6">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mr-4 energy-glow flex-shrink-0">
                      <Infinity className="w-6 h-6 text-energy" />
                    </div>
                    <div className="text-left">
                      <h4 className="text-lg font-semibold text-energy mb-1">Valid 1 Year</h4>
                      <p className="text-sm text-muted-foreground">
                        Your tokens are valid for one full year, giving you plenty of time to use them.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="obsidian-card">
                  <CardContent className="flex items-center p-6">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mr-4 energy-glow flex-shrink-0">
                      <Package className="w-6 h-6 text-energy" />
                    </div>
                    <div className="text-left">
                      <h4 className="text-lg font-semibold text-energy mb-1">Bundle Savings</h4>
                      <p className="text-sm text-muted-foreground">
                        Buy more tokens at once to save money. Larger bundles = bigger discounts.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="obsidian-card">
                  <CardContent className="flex items-center p-6">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mr-4 energy-glow flex-shrink-0">
                      <RotateCcw className="w-6 h-6 text-energy" />
                    </div>
                    <div className="text-left">
                      <h4 className="text-lg font-semibold text-energy mb-1">Total Flexibility</h4>
                      <p className="text-sm text-muted-foreground">
                        Switch between mentors, topics, or schedule sessions whenever it works for you.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-12 text-center">
                <Button size="lg" variant="outline" className="energy-glow w-full sm:w-auto max-w-md mx-auto text-sm sm:text-base px-4 sm:px-6" asChild>
                  <Link to="/auth/student">
                     <span className="hidden sm:inline">No subscriptions. No hidden fees.</span>
                     <span className="sm:hidden">No subscriptions</span>
                  </Link>
                </Button>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Credit Packages */}
          <Collapsible open={creditPackagesOpen} onOpenChange={setCreditPackagesOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between text-left p-4 sm:p-6 h-auto obsidian-card hover:bg-muted/5">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-glow pr-2">Token Packages</h3>
                <ChevronDown className={`h-5 w-5 sm:h-6 sm:w-6 text-primary transition-transform duration-200 flex-shrink-0 ${creditPackagesOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Micro tiers */}


                <Card className="relative obsidian-card">
                  <CardHeader className="text-center">
                    <CardTitle className="text-2xl text-energy">50 Tokens</CardTitle>
                    <div className="text-3xl font-bold text-primary">$50</div>
                    <p className="text-muted-foreground">Perfect for regular sessions</p>
                  </CardHeader>
                  <CardContent className="text-center">
                    <Button 
                      className="w-full energy-button" 
                      onClick={() => handlePurchase(50, Math.round(5000 * 1.175))}
                    >
                      Purchase
                    </Button>
                  </CardContent>
                </Card>

                {/* Core tiers (10x model) */}
                <Card className="relative obsidian-card">
                  <CardHeader className="text-center">
                    <CardTitle className="text-2xl text-energy">100 Tokens</CardTitle>
                    <div className="text-3xl font-bold text-primary">$100</div>
                    <p className="text-muted-foreground">Ideal for multiple sessions</p>
                  </CardHeader>
                  <CardContent className="text-center">
                    <Button 
                      className="w-full energy-button" 
                      onClick={() => handlePurchase(100, Math.round(10000 * 1.175))}
                    >
                      Purchase
                    </Button>
                  </CardContent>
                </Card>
                
                <Card className="relative obsidian-card border-primary shadow-lg energy-glow">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium energy-glow">
                      Most Popular
                    </span>
                  </div>
                  <CardHeader className="text-center">
                     <CardTitle className="text-2xl text-energy">500 Tokens</CardTitle>
                     <div className="text-3xl font-bold text-primary">$475</div>
                     <p className="text-muted-foreground">Save $25</p>
                  </CardHeader>
                  <CardContent className="text-center">
                    <Button 
                      className="w-full energy-button" 
                      onClick={() => handlePurchase(500, Math.round(47500 * 1.175))}
                    >
                      Purchase
                    </Button>
                  </CardContent>
                </Card>
                
                <Card className="relative obsidian-card">
                  <CardHeader className="text-center">
                     <CardTitle className="text-2xl text-energy">1000 Tokens</CardTitle>
                     <div className="text-3xl font-bold text-primary">$930</div>
                     <p className="text-muted-foreground">Save $70</p>
                  </CardHeader>
                  <CardContent className="text-center">
                    <Button 
                      className="w-full energy-button" 
                      onClick={() => handlePurchase(1000, Math.round(93000 * 1.175))}
                    >
                      Purchase
                    </Button>
                  </CardContent>
                </Card>
                
                <Card className="relative obsidian-card">
                  <CardHeader className="text-center">
                     <CardTitle className="text-2xl text-energy">2000 Tokens</CardTitle>
                     <div className="text-3xl font-bold text-primary">$1,860</div>
                     <p className="text-muted-foreground">Save $140</p>
                  </CardHeader>
                  <CardContent className="text-center">
                    <Button 
                      className="w-full energy-button" 
                      onClick={() => handlePurchase(2000, Math.round(186000 * 1.175))}
                    >
                      Purchase
                    </Button>
                  </CardContent>
                </Card>
                
                <Card className="relative obsidian-card">
                  <CardHeader className="text-center">
                     <CardTitle className="text-2xl text-energy">2400 Tokens</CardTitle>
                     <div className="text-3xl font-bold text-primary">$2,232</div>
                     <p className="text-muted-foreground">Save $168</p>
                  </CardHeader>
                  <CardContent className="text-center">
                    <Button 
                      className="w-full energy-button" 
                      onClick={() => handlePurchase(2400, Math.round(223200 * 1.175))}
                    >
                      Purchase
                    </Button>
                  </CardContent>
                </Card>
                
                <Card className="relative obsidian-card border-2 border-gold/50 shadow-xl">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-yellow-600 to-yellow-400 text-black px-4 py-1 rounded-full text-sm font-bold">
                      Best Value
                    </span>
                  </div>
                  <CardHeader className="text-center">
                     <CardTitle className="text-2xl text-energy">4800 Tokens</CardTitle>
                     <div className="text-3xl font-bold text-primary">$4,464</div>
                     <p className="text-muted-foreground">Save $336</p>
                  </CardHeader>
                  <CardContent className="text-center">
                    <Button 
                      className="w-full energy-button" 
                      onClick={() => handlePurchase(4800, Math.round(446400 * 1.175))}
                    >
                      Purchase
                    </Button>
                  </CardContent>
                </Card>
              </div>
              
              <div className="mt-12 text-center">
                <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-900/50 to-green-700/50 rounded-full border border-green-500/30">
                  <span className="text-green-300 font-semibold">✓ Buy Now, Pay Later options available with Klarna, Affirm & Afterpay</span>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </section>

      {/* Educational Disclaimer */}
      <section className="py-8 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
            <h4 className="font-bold text-primary mb-3 text-center flex items-center justify-center gap-2">
              🎓 100% Educational Platform
            </h4>
            <p className="text-muted-foreground text-center">
              We focus on skill development and knowledge transfer, not industry shortcuts or guaranteed opportunities. 
              This is <strong>NOT</strong> a pay-to-play service, collaboration marketplace, or ghost production platform. 
              Our mentors provide teaching and guidance - not guaranteed collaborations, placements, or industry connections.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <ComplianceFooter />
      
      {/* Cookie Consent */}
      <CookieConsent />
      
      {/* AI Mentor Matchmaker Chatbot */}
      <AIMentorChatbot userRole={userRole} />
    </div>
  );
};

export default Index;
